import promptsData from "../data/prompts.json";
import { bgImages } from "../constants";
import type { AiSelfieArt } from "./aiSelfieArtService";

const DEFAULT_STYLE_NAME = "Pencil Sketch";

type PromptStyle = {
  name: string;
  prompt: string;
  negative_prompt?: string;
};

/** URL slug (station `type`) → exact `name` in prompts.json */
const SLUG_TO_STYLE_NAME: Record<string, string> = {
  "pencil-sketch": "Pencil Sketch",
  "klimt-style": "Classic Art - Klimt Style #1",
  "botanical-surreal": "Classic Art - Botanical",
  "andy-warhol-marilyn-monroe-style": "Andy Warhol Marilyn Monroe Style",
  lichtenstein: "Pop Art - Lichtenstein",
  "wynwood-caricature": "Caricature",
  wynwood: "Wynwood Art",
  "90-cartoon": "Cartoon (90s Anime)",
  cartoon: "Cartoon (90s Anime)",
  caricature: "Caricature",
  "famous-painting": "Andy Warhol Marilyn Monroe Style",
  "graffiti-monochrome": "Graffiti Art - Monochrome",
  "pop-graffiti": "Graffiti Art - Pop Graffiti",
  "neon-surreal-graffiti": "Graffiti Art - Neon",
};

export type TransformationUiAssetKey =
  | "pencil-sketch"
  | "caricature"
  | "cartoon"
  | "wynwood"
  | "famous-painting";

export type TransformationProcessPreset = {
  backgroundUrl?: string;
  canvasSize: string;
  removeBg: boolean;
  uiAssetKey: TransformationUiAssetKey;
};

const stylesList = promptsData.styles as PromptStyle[];

function getStyleEntryByName(name: string): PromptStyle | undefined {
  return stylesList.find((s) => s.name === name);
}

/** Set after user picks a hobby on `/:slug/select-hobby` (AI selfie `is_caricature_hobby` flow). */
export const AI_SELFIE_CARICATURE_HOBBY_PROMPT_KEY = "aiSelfieCaricatureHobbyPrompt";

export function readAiSelfieCaricatureHobbyPrompt(): string | null {
  try {
    const raw = localStorage.getItem(AI_SELFIE_CARICATURE_HOBBY_PROMPT_KEY);
    if (typeof raw !== "string") return null;
    const t = raw.trim();
    return t.length ? t : null;
  } catch {
    return null;
  }
}

export function clearAiSelfieCaricatureHobbyPrompt(): void {
  try {
    localStorage.removeItem(AI_SELFIE_CARICATURE_HOBBY_PROMPT_KEY);
  } catch {
    /* ignore */
  }
}

function appendNegativePromptSegment(body: string, negative: string | undefined): string {
  const n = typeof negative === "string" ? negative.trim() : "";
  if (!n) return body;
  return `${body} Negative prompt: ${n}`;
}

const NEGATIVE_PROMPT_MARKER = " Negative prompt: ";

/**
 * Appends an optional user-authored line into the positive prompt segment.
 * Empty/whitespace input leaves the prompt unchanged. When a negative-prompt
 * suffix is present, the line is inserted before it so it stays part of the
 * generation instructions (not the negatives).
 *
 * The line is treated as a creative instruction only — it must not be rendered
 * as visible text/caption on the generated artwork.
 */
export function appendOptionalUserLineToPrompt(prompt: string, userLine: string | undefined | null): string {
  const line = typeof userLine === "string" ? userLine.trim() : "";
  if (!line) return prompt;

  const instruction =
    ` Additionally apply this creative instruction to the artwork (do not write or render this text anywhere in the image): ${line}.`;
  const base = prompt || "";
  const negIndex = base.indexOf(NEGATIVE_PROMPT_MARKER);

  if (negIndex === -1) {
    const trimmed = base.trim();
    if (!trimmed) return instruction.trim();
    return `${trimmed.replace(/[.]+$/, "")}.${instruction}`;
  }

  const positive = base.slice(0, negIndex).trim().replace(/[.]+$/, "");
  const negative = base.slice(negIndex);
  return `${positive}.${instruction}${negative}`;
}

/** API may send boolean, 0/1, or string flags for `is_caricature_hobby`. */
export function isCaricatureHobbyAiSelfieArt(
  art: { is_caricature_hobby?: unknown } | null | undefined
): boolean {
  const v = art?.is_caricature_hobby;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** API may send boolean, 0/1, or string flags for `is_fifa`. */
export function isFifaAiSelfieArt(
  art: { is_fifa?: unknown } | null | undefined
): boolean {
  const v = art?.is_fifa;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** After print-payment, route through `hobby-caricature` people → media → hobby before upload. */
export const AI_SELFIE_CARICATURE_HOBBY_POST_PAYMENT_KEY = "aiSelfieCaricatureHobbyPostPayment";

export function readSelectedAiSelfieArtFromStorage(): Partial<AiSelfieArt> | null {
  try {
    const raw = localStorage.getItem("selectedAiSelfieArt");
    if (!raw) return null;
    return JSON.parse(raw) as Partial<AiSelfieArt>;
  } catch {
    return null;
  }
}

/** `type` / `favouriteType` for favourites API — always prefer selected art name. */
export function resolveSelectedAiSelfieArtFavouriteType(
  selectedArt?: { name?: string | null } | null
): string {
  const art = selectedArt !== undefined ? selectedArt : readSelectedAiSelfieArtFromStorage();
  const name = art?.name?.trim();
  if (name) return name;

  const station = readStationDataFromStorage();
  return station?.name?.trim() || station?.type?.trim() || "hobby caricature";
}

/** FIFA route slug: art name when `is_fifa`, else station `type`, else default. */
export function resolveFifaStationSlug(
  station?: { type?: string } | null,
  selectedArt?: { is_fifa?: unknown; name?: string | null } | null
): string {
  const art = selectedArt !== undefined ? selectedArt : readSelectedAiSelfieArtFromStorage();
  if (isFifaAiSelfieArt(art) && art?.name?.trim()) {
    return art.name.trim().toLowerCase().replace(/\s+/g, "-");
  }
  return station?.type?.trim() || "";
}

/**
 * Selected AI selfie art has `is_caricature_hobby` — use people → photos →
 * caricature hobby categories/hobbies → batch generate (under the art's own slug),
 * not `/${categorySlug}/image-transformation`.
 */
export function shouldUseHobbyCaricatureFlowForStoredAiSelfieArt(): boolean {
  const art = readSelectedAiSelfieArtFromStorage();
  if (isFifaAiSelfieArt(art)) {
    return false;
  }
  return isCaricatureHobbyAiSelfieArt(art);
}

/** True when art name slugifies to `pet-caricature` (e.g. "Pet Caricature"). */
export function isPetCaricatureAiSelfieArt(
  art: { name?: string | null } | null | undefined
): boolean {
  return slugifyRouteSegment(art?.name || "") === "pet-caricature";
}

/**
 * Pet-only background flow (generate → background → save-favorite → print).
 * Never true for hobby caricature or FIFA arts. Prefer selected art name;
 * station `type` is a fallback only (not display name, to avoid false matches).
 */
export function shouldUsePetCaricatureBackgroundFlow(
  selectedArt?: {
    name?: string | null;
    is_caricature_hobby?: unknown;
    is_fifa?: unknown;
  } | null,
  station?: { type?: string | null; name?: string | null } | null
): boolean {
  const art =
    selectedArt !== undefined ? selectedArt : readSelectedAiSelfieArtFromStorage();

  // Other special flows must keep their own routing.
  if (isFifaAiSelfieArt(art) || isCaricatureHobbyAiSelfieArt(art)) {
    return false;
  }

  if (isPetCaricatureAiSelfieArt(art)) {
    return true;
  }

  // Fallback: dedicated station type only (do not match on display name).
  const stationData =
    station !== undefined ? station : readStationDataFromStorage();
  return slugifyRouteSegment(stationData?.type || "") === "pet-caricature";
}

/**
 * Caricature Portrait art / station (`/caricature-portrait/idle`).
 * Start Now routes to choose-option (take photo / upload).
 */
export function isCaricaturePortraitFlow(input?: {
  slug?: string | null;
  stationType?: string | null;
  artName?: string | null;
} | null): boolean {
  const slug = String(input?.slug || "").trim().toLowerCase();
  const type = String(input?.stationType || "").trim().toLowerCase();
  const name = String(input?.artName || "").trim().toLowerCase();

  if (slug === "caricature-portrait") return true;
  if (type === "caricature-portrait") return true;
  if (name.includes("caricature") && name.includes("portrait")) return true;

  if (!input?.artName && !input?.stationType) {
    const art = readSelectedAiSelfieArtFromStorage();
    const station = readStationDataFromStorage();
    const artName = String(art?.name || "").trim().toLowerCase();
    const stationType = String(station?.type || "").trim().toLowerCase();
    if (stationType === "caricature-portrait") return true;
    if (artName.includes("caricature") && artName.includes("portrait")) return true;
  }

  return false;
}

function matchesPrintYourOwnToken(value: string): boolean {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return false;
  // Primary: Print Your Own
  if (v === "print-your-own" || v === "printyourown" || v === "print your own") return true;
  if (v.includes("print-your-own") || v.includes("print your own")) return true;
  // Legacy Snap Print / Snap and Print aliases
  if (
    v === "snap-print" ||
    v === "snapprint" ||
    v === "snap print" ||
    v === "snap-and-print" ||
    v === "snap and print"
  ) {
    return true;
  }
  if (
    v.includes("snap-print") ||
    v.includes("snap print") ||
    v.includes("snap-and-print") ||
    v.includes("snap and print")
  ) {
    return true;
  }
  return false;
}

/**
 * Print Your Own art / station (`/print-your-own/choose-option`).
 * Capture/upload without BG removal; optional BG remove is chosen on `/bg-remove`.
 * Also matches legacy `snap-print` slugs/names.
 */
export function isPrintYourOwnFlow(input?: {
  slug?: string | null;
  stationType?: string | null;
  artName?: string | null;
} | null): boolean {
  const slug = String(input?.slug || "").trim().toLowerCase();
  const type = String(input?.stationType || "").trim().toLowerCase();
  const name = String(input?.artName || "").trim().toLowerCase();

  if (
    matchesPrintYourOwnToken(slug) ||
    matchesPrintYourOwnToken(type) ||
    matchesPrintYourOwnToken(name)
  ) {
    return true;
  }

  if (!input?.artName && !input?.stationType && !input?.slug) {
    const art = readSelectedAiSelfieArtFromStorage();
    const station = readStationDataFromStorage();
    return (
      matchesPrintYourOwnToken(String(station?.type || "")) ||
      matchesPrintYourOwnToken(String(art?.name || ""))
    );
  }

  return false;
}

/** @deprecated Use isPrintYourOwnFlow — kept for existing imports. */
export const isSnapPrintFlow = isPrintYourOwnFlow;

export type PortraitSubject = "people" | "pet";

const PORTRAIT_SUBJECT_KEY = "portraitSubject";

export function persistPortraitSubject(subject: PortraitSubject): void {
  try {
    localStorage.setItem(PORTRAIT_SUBJECT_KEY, subject);
  } catch {
    /* ignore */
  }
}

export function readPortraitSubject(): PortraitSubject | null {
  try {
    const value = localStorage.getItem(PORTRAIT_SUBJECT_KEY);
    if (value === "people" || value === "pet") return value;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * 90's cartoon art / station (`/90-s-cartoon/idle`, type `90-cartoon`).
 * Start Now routes to select-people-cartoon (not choose-option).
 */
export function is90sCartoonFlow(input?: {
  slug?: string | null;
  stationType?: string | null;
  artName?: string | null;
} | null): boolean {
  const slug = String(input?.slug || "").trim().toLowerCase();
  const type = String(input?.stationType || "").trim().toLowerCase();
  const name = String(input?.artName || "").trim().toLowerCase();

  if (slug === "90-s-cartoon" || slug === "90-cartoon") return true;
  if (type === "90-cartoon" || type === "90-s-cartoon") return true;
  if (slug.includes("90") && slug.includes("cartoon")) return true;
  if (name.includes("90") && name.includes("cartoon")) return true;

  // Fall back to stored art / station when callers only pass a route slug.
  if (!input?.artName && !input?.stationType) {
    const art = readSelectedAiSelfieArtFromStorage();
    const station = readStationDataFromStorage();
    const artName = String(art?.name || "").trim().toLowerCase();
    const stationType = String(station?.type || "").trim().toLowerCase();
    if (stationType === "90-cartoon" || stationType === "90-s-cartoon") return true;
    if (artName.includes("90") && artName.includes("cartoon")) return true;
  }

  return false;
}

/** Parent category slugs — never use these for hobby-caricature child flow routes. */
const HOBBY_PARENT_CATEGORY_SLUGS = new Set(["caricature-cartoon"]);

function isHobbyParentCategorySlug(value: string): boolean {
  return HOBBY_PARENT_CATEGORY_SLUGS.has(value.trim().toLowerCase());
}

/**
 * URL slug for caricature-hobby flow pages. Prefer the current route slug
 * (e.g. hobby-caricature / wynwood-caricature), then selected art name —
 * never keep the parent category `caricature-cartoon`.
 */
export function resolveHobbyCaricatureRouteSlug(
  routeSlug?: string | null
): string {
  const fromRoute = routeSlug?.trim() || "";
  if (fromRoute && !isHobbyParentCategorySlug(fromRoute)) return fromRoute;

  const art = readSelectedAiSelfieArtFromStorage();
  const fromArt = slugifyRouteSegment(art?.name || "");
  if (fromArt && !isHobbyParentCategorySlug(fromArt)) return fromArt;

  const station = readStationDataFromStorage();
  const fromStation = station?.type?.trim() || "";
  if (fromStation && !isHobbyParentCategorySlug(fromStation)) return fromStation;

  return "hobby-caricature";
}

export function setAiSelfieCaricatureHobbyPostPaymentActive(): void {
  try {
    localStorage.setItem(AI_SELFIE_CARICATURE_HOBBY_POST_PAYMENT_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function readStationDataFromStorage(): { type?: string; name?: string } {
  try {
    const selectedRaw = localStorage.getItem("selectedStation");
    const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
    const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
    return fromSelected || fromStorage || {};
  } catch {
    return {};
  }
}

function slugifyRouteSegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Route after onsite payment when skipping the print-receipt screen. */
export function resolvePostOnsitePaymentSuccessRoute(): string {
  const stationData = readStationDataFromStorage();
  const art = readSelectedAiSelfieArtFromStorage();
  const isHobby = isCaricatureHobbyAiSelfieArt(art);
  const isFifa = isFifaAiSelfieArt(art);

  if (art && !isHobby && !isFifa) {
    const slug =
      slugifyRouteSegment(art.name || "") ||
      stationData?.type?.trim() ||
      "pencil-sketch";
    return `/${slug}/choose-option`;
  }

  if (isFifa) {
    return `/${resolveFifaStationSlug(stationData, art)}/select-fifa-card`;
  }

  if (stationData?.type === "hobby-caricature" || (art && isHobby)) {
    if (art && isHobby) {
      clearAiSelfieCaricatureHobbyPrompt();
      setAiSelfieCaricatureHobbyPostPaymentActive();
    }
    return `/${resolveHobbyCaricatureRouteSlug()}/select-people`;
  }

  const fallbackSlug = stationData?.type?.trim() || "pencil-sketch";
  return `/${fallbackSlug}/choose-option`;
}

export function clearAiSelfieCaricatureHobbyPostPayment(): void {
  try {
    localStorage.removeItem(AI_SELFIE_CARICATURE_HOBBY_POST_PAYMENT_KEY);
  } catch {
    /* ignore */
  }
}

export function isAiSelfieCaricatureHobbyPostPaymentActive(): boolean {
  try {
    return localStorage.getItem(AI_SELFIE_CARICATURE_HOBBY_POST_PAYMENT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Slug used for process preset + layout when the selected art is a caricature hobby style.
 */
export function effectiveStyleSlugForAiSelfieArt(
  art: { is_caricature_hobby?: unknown } | null | undefined,
  categorySlug: string | undefined
): string | undefined {
  return isCaricatureHobbyAiSelfieArt(art) ? "caricature" : categorySlug;
}

/**
 * Builds the generation prompt: prefers API `prompt` / hobby override, else `prompts.json`.
 * For non–caricature-hobby with a reference art image, this string is sent as `reference_prompt`.
 */
export function buildCharacterPromptForArt(
  art: {
    prompt?: string | null;
    negative_prompt?: string | null;
    is_caricature_hobby?: unknown;
    name?: string | null;
  } | null | undefined,
  slugFallback: string | undefined
): string {
  const neg = typeof art?.negative_prompt === "string" ? art.negative_prompt.trim() : "";
  const primary = typeof art?.prompt === "string" ? art.prompt.trim() : "";

  if (isCaricatureHobbyAiSelfieArt(art)) {
    const fromHobby = readAiSelfieCaricatureHobbyPrompt();
    if (fromHobby) {
      return appendNegativePromptSegment(fromHobby, neg || undefined);
    }
  }

  // Prefer AI Station admin prompt (`art.prompt`) for all arts, including caricature-portrait.
  if (primary) {
    return appendNegativePromptSegment(primary, neg || undefined);
  }
  if (isCaricatureHobbyAiSelfieArt(art)) {
    return resolvePromptForSlug("caricature");
  }
  return resolvePromptForSlug(slugFallback);
}

/**
 * Resolves the prompt string from the URL slug (`stationData.type`) for legacy flows.
 * Unknown slugs use Pencil Sketch. If the mapped style name is missing from JSON, falls back to Pencil Sketch.
 */
export function resolvePromptForSlug(slug: string | undefined): string {
  const mapped =
    slug && SLUG_TO_STYLE_NAME[slug] !== undefined
      ? SLUG_TO_STYLE_NAME[slug]
      : DEFAULT_STYLE_NAME;

  let entry = getStyleEntryByName(mapped);
  if (!entry) {
    entry = getStyleEntryByName(DEFAULT_STYLE_NAME);
  }
  if (!entry) return "";

  if (entry.negative_prompt) {
    return `${entry.prompt} Negative prompt: ${entry.negative_prompt}`;
  }
  return entry.prompt;
}

// export function usesWideResultLayout(slug: string | undefined): boolean {
//   if (!slug) return false;
//   return (
//     slug === "caricature" ||
//     slug === "wynwood-caricature" ||
//     slug === "cartoon" ||
//     slug === "90-cartoon"
//   );
// }

function normalizeArtLayoutText(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[''`´]/g, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Caricature and cartoon arts use a 3/2 frame; all other arts stay 1:1. */
export function isCaricatureOrCartoonArt(...parts: unknown[]): boolean {
  const text = normalizeArtLayoutText(parts.filter(Boolean).join(" "));
  return text.includes("caricature") || text.includes("cartoon");
}

/** Optional "Write your line" limit: caricature/cartoon 45, all other textarea arts 30. */
export function getTransformCustomLineMaxLength(...parts: unknown[]): number {
  return isCaricatureOrCartoonArt(...parts) ? 45 : 30;
}

/**
 * Caption font for the optional user line overlay:
 * - Caricature / cartoon → Bangers
 * - Graffiti art → Permanent Marker
 * - Pop art (Warhol, Lichtenstein, etc.) → Anton
 * - Everything else → Inter
 */
export function getTransformCustomLineFontFamily(...parts: unknown[]): string {
  const text = normalizeArtLayoutText(parts.filter(Boolean).join(" "));
  if (text.includes("caricature") || text.includes("cartoon")) {
    return "Bangers, system-ui, sans-serif";
  }
  if (
    text.includes("graffiti") ||
    text.includes("wynwood") ||
    text.includes("neon-surreal")
  ) {
    return '"Permanent Marker", cursive';
  }
  if (
    text.includes("warhol") ||
    text.includes("marilyn") ||
    text.includes("lichtenstein") ||
    text.includes("pop-art") ||
    text.includes("pop art") ||
    text.includes("famous-painting")
  ) {
    return "Anton, Impact, sans-serif";
  }
  return "Inter, system-ui, sans-serif";
}

/** Background + canvas flags + placeholder art bucket for the transformation UI. */
export function getProcessPresetForSlug(
  slug: string | undefined
): TransformationProcessPreset {
  const s = slug || "";

  // if (s === "caricature" || s === "wynwood-caricature") {
  //   return {
  //     backgroundUrl: bgImages.caricature,
  //     canvasSize: "16/9",
  //     removeBg: true,
  //     uiAssetKey: "caricature",
  //   };
  // }
  // if (s === "cartoon" || s === "90-cartoon") {
  //   return {
  //     backgroundUrl: bgImages.cartoon,
  //     canvasSize: "16/9",
  //     removeBg: true,
  //     uiAssetKey: "cartoon",
  //   };
  // }
  if (s === "wynwood") {
    return {
      canvasSize: "8x8",
      removeBg: false,
      uiAssetKey: "wynwood",
    };
  }
  if (
    s === "famous-painting" ||
    s === "andy-warhol-marilyn-monroe-style" ||
    s === "lichtenstein"
  ) {
    return {
      canvasSize: "8x8",
      removeBg: false,
      uiAssetKey: "famous-painting",
    };
  }
  if (s === "klimt-style" || s === "botanical-surreal") {
    return {
      canvasSize: "8x8",
      removeBg: false,
      uiAssetKey: "famous-painting",
    };
  }
  if (
    s === "graffiti-monochrome" ||
    s === "pop-graffiti" ||
    s === "neon-surreal-graffiti"
  ) {
    return {
      canvasSize: "8x8",
      removeBg: false,
      uiAssetKey: "wynwood",
    };
  }

  return {
    canvasSize: "8x8",
    removeBg: false,
    uiAssetKey: "pencil-sketch",
  };
}
